import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaBrain, FaQuestionCircle, FaFileContract, FaSignOutAlt, FaCrown, FaHistory, FaFile, FaInfoCircle, FaTimes, FaChalkboardTeacher, FaEdit, FaLinkedin } from 'react-icons/fa';
import { HiAcademicCap, HiSparkles } from 'react-icons/hi';
import { getUserProfile, removeSkill } from '../services/userService';
import { getMentorProfile, updateMentorProfile } from '../services/mentorService';


const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [mentorProfile, setMentorProfile] = useState(null); // New state for mentor profile
  const [loading, setLoading] = useState(true);
  const [editMentorModalOpen, setEditMentorModalOpen] = useState(false);
  const [mentorForm, setMentorForm] = useState({
    bio: '',
    expertise: '',
    experience: '',
    linkedin: '',
    mobile: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null); // For modal/popover if needed, or just direct share logic

  const { user, setUser, logout } = useAuth();
  const { showAlert } = useAlertContext();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch User Profile
        const userData = await getUserProfile();
        setProfile(userData.user);

        // Fetch Mentor Profile (will fail 404 if not mentor, which is fine)
        try {
          const mentorData = await getMentorProfile();
          if (mentorData.success) {
            setMentorProfile(mentorData.mentor);
            setMentorForm({
              bio: mentorData.mentor.bio || '',
              expertise: mentorData.mentor.expertise || '',
              experience: mentorData.mentor.experience || '',
              linkedin: mentorData.mentor.linkedin || '',
              mobile: mentorData.mentor.mobile || ''
            });
          }
        } catch (e) {
          // User is not a mentor, ignore
        }

      } catch (err) {
        console.error('Failed to load profile', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpdateMentorProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('bio', mentorForm.bio);
      formData.append('expertise', mentorForm.expertise);
      formData.append('experience', mentorForm.experience);
      formData.append('linkedin', mentorForm.linkedin);
      formData.append('mobile', mentorForm.mobile);
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const data = await updateMentorProfile(formData);
      
      if (data.success) {
        setMentorProfile(data.mentor);
        setEditMentorModalOpen(false);
        setImageFile(null);
        setImagePreview(null);
        showAlert({ type: 'success', message: 'Mentor profile updated successfully!' });
      }
    } catch (error) {
       console.error(error);
       showAlert({ type: 'error', message: 'Failed to update profile.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRemoveSkill = async (skillId) => {
    if (!skillId || isRemoving) return;
    
    try {
      setIsRemoving(true);
      const data = await removeSkill(skillId);
      
      if (data.success) {
        // Update local state to remove the skill
        setProfile(prev => ({
          ...prev,
          skills: prev.skills.filter(skill => skill._id !== skillId)
        }));
      }
    } catch (err) {
      console.error('Error removing skill:', err);
      alert('Failed to remove skill. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  };

  // Helper to check if user is premium
  const isPremium = true; // Everyone is free/premium now
  const planLabel = 'Member';

  const handleShareBadge = (badge) => {
    const shareUrl = "https://gaplify.in/daily-quiz";
    const text = `Excited to share that I've unlocked the "${badge.name}" badge on Gaplify! 🏆\n\n${badge.description}\n\nI'm challenging myself to improve my skills daily. Join me and test your knowledge! 👇\n${shareUrl}\n\n#Gaplify #DailyQuiz #LearningJourney #AchievementUnlocked`;
    
    // Open LinkedIn Feed with pre-filled text
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`, '_blank');
  };
  
  // Helper to get unique skills by name (case-insensitive)
  const getUniqueSkills = (skills) => {
    const uniqueSkills = new Map();
    skills?.forEach(skill => {
      if (skill?.name) {
        const key = skill.name.trim().toLowerCase();
        if (!uniqueSkills.has(key)) {
          uniqueSkills.set(key, skill);
        }
      }
    });
    return Array.from(uniqueSkills.values());
  };
  
  // Get unique skills for display
  const uniqueSkills = getUniqueSkills(profile?.skills);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // User Card Section
  const userCardSection = (
    <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center relative overflow-hidden border border-white/50">
        <div className="w-28 h-28 md:w-36 md:h-36 mx-auto bg-gradient-to-br from-primary-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-4xl md:text-5xl font-bold shadow-2xl mb-6 ring-4 ring-white/50">
            {profile?.username ? profile.username.charAt(0).toUpperCase() : 'U'}
        </div>
        <h2 className="heading-secondary text-gray-900 mb-1">{profile?.username || user?.name || 'User'}</h2>
        <p className="text-gray-500 mb-8 font-medium flex items-center justify-center">
            <FaEnvelope className="mr-2 text-primary-400" /> {profile?.email || user?.email}
        </p>
        
        <div className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-6">
            <div className="text-center group cursor-pointer hover:bg-gray-50 rounded-xl p-2 transition-colors">
                <span className="block text-xl md:text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{profile?.skills?.length || 0}</span>
                <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Skills</span>
            </div>
            <div className="text-center border-l border-gray-100 group cursor-pointer hover:bg-gray-50 rounded-xl p-2 transition-colors">
                    <span className="block text-xl md:text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{profile?.roadmapGenCount || 0}</span>
                    <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Roadmaps</span>
            </div>
            <div className="text-center border-l border-gray-100 group cursor-pointer hover:bg-gray-50 rounded-xl p-2 transition-colors">
                    <span className="block text-xl md:text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{profile?.skillAnalysisCount || 0}</span>
                    <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Analysis</span>
            </div>
        </div>
    </div>
  );

  // Navigation Links Section
  const navLinksSection = (
    <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-6 border border-white/50">
        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 ml-2">Quick Actions</h3>
        <div className="space-y-2">
            {user?.isMentor ? (
                    <button onClick={() => navigate('/mentor/dashboard')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-2xl transition-all font-semibold group border border-dashed border-indigo-200 bg-indigo-50/50">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mr-3 group-hover:bg-indigo-200 transition-colors">
                        <FaChalkboardTeacher className="w-4 h-4" />
                    </div>
                    Mentor Dashboard
                </button>
            ) : (
                    <button onClick={() => navigate('/become-mentor')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-2xl transition-all font-semibold group border border-dashed border-indigo-200 bg-indigo-50/50">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mr-3 group-hover:bg-indigo-200 transition-colors">
                        <FaChalkboardTeacher className="w-4 h-4" />
                    </div>
                    Become a Mentor
                </button>
            )}
            <button onClick={() => navigate('/history')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-2xl transition-all font-semibold group">
                <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mr-3 group-hover:bg-primary-200 transition-colors">
                    <FaHistory className="w-4 h-4" />
                </div>
                History
            </button>
            <button onClick={() => navigate('/feedback')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-2xl transition-all font-semibold group">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mr-3 group-hover:bg-blue-200 transition-colors">
                    <FaInfoCircle className="w-4 h-4" />
                </div>
                Feedback
            </button>
                <button onClick={() => navigate('/privacy-policy')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-2xl transition-all font-semibold group">
                <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center mr-3 group-hover:bg-green-200 transition-colors">
                    <FaFileContract className="w-4 h-4" />
                </div>
                    Privacy Policy
            </button>
            <button onClick={() => navigate('/faq')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-2xl transition-all font-semibold group">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mr-3 group-hover:bg-purple-200 transition-colors">
                    <FaQuestionCircle className="w-4 h-4" />
                </div>
                FAQ
            </button>
            <button onClick={() => navigate('/terms-and-conditions')} className="w-full flex items-center px-4 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-2xl transition-all font-semibold group">
                    <div className="w-8 h-8 rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center mr-3 group-hover:bg-yellow-200 transition-colors">
                    <FaFile className="w-4 h-4" />
                </div>
                Terms & Conditions
            </button>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-100">
                <button onClick={handleLogout} className="w-full flex items-center px-4 py-3.5 text-red-600 hover:bg-red-50 hover:scale-[1.02] active:scale-95 rounded-2xl transition-all font-bold shadow-sm border border-transparent hover:border-red-100">
                <FaSignOutAlt className="w-5 h-5 mr-3" /> Log Out
            </button>
        </div>
    </div>
  );

  // Welcome Card Section
  const welcomeCardSection = (
    <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-white/50 relative overflow-hidden group">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-100 to-primary-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3 group-hover:scale-110 transition-transform duration-700"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
                    <h1 className="heading-secondary text-gray-900 mb-2">
                    Welcome Back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">{profile?.username || 'User'}</span>! 👋
                    </h1>
                    <p className="text-gray-600 text-lg">
                    Ready to take your career to the next level? You've made great progress.
                    </p>
            </div>
            <div className="hidden md:block">
                <HiAcademicCap className="w-24 h-24 text-primary-200" />
            </div>
            </div>
    </div>
  );

  // Badges Section
  const badgesSection = (
    <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50 mb-8">
        <h2 className="heading-tertiary text-gray-900 flex items-center mb-6">
            <FaCrown className="text-yellow-500 mr-3" /> Achievements
        </h2>
        
        {profile?.badges && profile.badges.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {profile.badges.map((badge, idx) => (
                    <div 
                        key={idx} 
                        className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all text-center group cursor-pointer relative"
                        onClick={() => handleShareBadge(badge)}
                        title="Click to share on LinkedIn"
                    >
                        <div className="text-4xl mb-2 transform group-hover:scale-110 transition-transform duration-300">{badge.icon || '🏅'}</div>
                        <h4 className="font-bold text-gray-900 text-sm mb-1">{badge.name}</h4>
                        <p className="text-[10px] text-gray-500 leading-tight">{badge.description}</p>
                        
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <FaLinkedin className="text-blue-600 w-4 h-4" />
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaCrown className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-sm">No badges yet. Keep taking daily quizzes!</p>
            </div>
        )}
    </div>
  );

  // Skills Section
  const skillsSection = (
    <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50">
        <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="heading-tertiary text-gray-900 flex items-center">
                    <FaBrain className="text-primary-500 mr-3" /> My Skill Bridge
                </h2>
                <p className="text-sm text-gray-500 font-medium">Manage the skills you are mastering</p>
                </div>
                <button 
                onClick={() => navigate('/skills-analysis')}
                className="px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-500/30 flex items-center"
                >
                <HiSparkles className="mr-2" /> Add New Skills
                </button>
        </div>

        {uniqueSkills && uniqueSkills.length > 0 ? (
            <div className="flex flex-wrap gap-4">
                {uniqueSkills.map((skill, idx) => (
                    <div key={skill._id || idx} className="group relative w-full sm:w-auto bg-white border border-gray-100 rounded-2xl px-5 py-4 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
                        <div className="flex items-center space-x-3">
                            <div className={`w-2 h-10 rounded-full ${
                                skill.proficiency === 'Beginner' ? 'bg-green-500' :
                                skill.proficiency === 'Intermediate' ? 'bg-yellow-500' :
                                'bg-red-500'
                            }`}></div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg leading-tight">{skill.name}</h4>
                                <span className={`text-[10px] font-bold uppercase tracking-wide text-gray-400`}>
                                    {skill.proficiency} Level
                                </span>
                            </div>
                        </div>
                            <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSkill(skill._id);
                            }}
                            className="absolute -top-2 -right-2 w-8 h-8 bg-white text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-md hover:bg-red-50 border border-red-100"
                            title="Remove skill"
                            >
                            <FaTimes className="w-4 h-4" />
                            </button>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HiSparkles className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">No skills added yet</h3>
                    <p className="text-gray-500 mb-6">Start your journey by analyzing your current skillset.</p>
                    <button 
                    onClick={() => navigate('/skills-analysis')}
                    className="btn btn-primary"
                    >
                    Start Analysis
                    </button>
            </div>
        )}
    </div>
  );

  // Mentor Section
  const mentorSection = mentorProfile ? (
        <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-secondary-100 rounded-bl-full opacity-50"></div>
            
            <div className="flex flex-col md:flex-row justify-between items-start mb-8 relative z-10">
            <div>
                    <h2 className="heading-tertiary text-gray-900 flex items-center">
                    <FaChalkboardTeacher className="text-secondary-500 mr-3" /> Mentor Profile
                </h2>
                <p className="text-sm text-gray-500 font-medium">This is how students see you on the platform</p>
            </div>
            <button 
                onClick={() => setEditMentorModalOpen(true)}
                className="mt-4 md:mt-0 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all shadow-sm flex items-center group"
            >
                <FaEdit className="mr-2 text-gray-400 group-hover:text-primary-600" /> Edit Profile
            </button>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 shadow-inner">
                <div className="flex flex-col md:flex-row gap-8">
                    <div className="flex-shrink-0">
                        <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg border-4 border-white ring-1 ring-gray-100 bg-gray-200">
                                {mentorProfile.avatar && mentorProfile.avatar.startsWith('http') ? (
                                <img src={mentorProfile.avatar} alt="Mentor Avatar" className="w-full h-full object-cover" />
                                ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                                    <FaUser className="w-12 h-12" />
                                </div>
                                )}
                        </div>
                    </div>
                    <div className="flex-1 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Expertise</h4>
                                <p className="font-bold text-gray-900 text-lg">{mentorProfile.expertise || 'Not specified'}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Experience</h4>
                                <p className="font-bold text-gray-900 text-lg">{mentorProfile.experience || 'N/A'}</p>
                            </div>
                        </div>
                        
                            <div>
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">About Me</h4>
                            <p className="text-gray-700 leading-relaxed bg-white p-4 rounded-xl border border-gray-100 text-sm">
                                {mentorProfile.bio || 'No bio provided.'}
                            </p>
                        </div>
                        
                        {mentorProfile.linkedin && (
                            <div className="pt-2">
                                <a href={mentorProfile.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-bold hover:underline">
                                    <FaLinkedin className="mr-2" /> View LinkedIn Profile
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
  ) : (
    <div className="relative rounded-3xl overflow-hidden shadow-2xl group cursor-pointer" onClick={() => navigate('/become-mentor')}>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800"></div>
        <div className="absolute right-0 top-0 h-full w-2/3 bg-gradient-to-l from-primary-900/50 to-transparent"></div>
        
        <div className="relative z-10 p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-md">
                <div className="inline-block px-3 py-1 bg-white/10 rounded-full text-xs font-bold text-primary-300 mb-4 border border-white/10">Become a Guide</div>
                <h2 className="heading-secondary text-white mb-3">Join our Mentor Network</h2>
                <p className="text-gray-400 mb-8 leading-relaxed">Share your industry knowledge, guide future professionals, and build your personal brand.</p>
                <button
                    onClick={(e) => { e.stopPropagation(); navigate('/become-mentor'); }}
                    className="px-8 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-white/20 hover:-translate-y-1"
                >
                    Apply Now
                </button>
            </div>
            <div className="relative">
                <div className="w-32 h-32 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/10 group-hover:scale-110 transition-transform duration-500">
                    <FaChalkboardTeacher className="w-14 h-14 text-white" />
                </div>
            </div>
        </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
      `
    }}>
      {/* Top Decoration */}
      <div className="h-[40rem]  bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
         <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      <div className="container-responsive relative z-10 pt-24 md:pt-28 px-4 sm:px-6 lg:px-8 pb-12">
           {/* Mobile View Strategy: Just render flex col with specific order */}
           <div className="flex flex-col space-y-6 lg:hidden">
              {welcomeCardSection}
              {userCardSection}
              {badgesSection}
              {navLinksSection}
              {skillsSection}
              {mentorSection}
          </div>

          {/* Desktop View Strategy: Grid Columns - Original Layout */}
          <div className="hidden lg:grid grid-cols-12 gap-8">
              {/* Left Sidebar - User Profile & Navigation */}
              <div className="col-span-4 space-y-6">
                  {userCardSection}
                  {navLinksSection}
              </div>

              {/* Right Content */}
              <div className="col-span-8 space-y-8">
                  {welcomeCardSection}
                  {badgesSection}
                  {skillsSection}
                  {mentorSection}
              </div>
          </div>
      </div>
      
      {/* Edit Mentor Modal */}
      {editMentorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-[100] p-4 transition-all">
          <div className="bg-white rounded-3xl w-full max-w-lg p-0 max-h-[90vh] overflow-y-auto relative shadow-2xl animate-in fade-in zoom-in duration-300 mx-4">
             
             {/* Modal Header */}
             <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h2 className="heading-tertiary text-gray-900">Edit Mentor Profile</h2>
                 <button
                  className="p-2 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setEditMentorModalOpen(false)}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
             </div>
            
            <form onSubmit={handleUpdateMentorProfile} className="p-8 space-y-6">
              {/* Image Upload */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative group cursor-pointer">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg ring-2 ring-primary-100 group-hover:ring-primary-300 transition-all">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : mentorProfile?.avatar && mentorProfile.avatar.startsWith('http') ? (
                       <img src={mentorProfile.avatar} alt="Current" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <FaUser className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <label 
                    htmlFor="profile-image-upload" 
                    className="absolute bottom-1 right-1 bg-primary-600 text-white p-3 rounded-full cursor-pointer hover:bg-primary-700 shadow-lg hover:scale-110 transition-all border-4 border-white"
                    title="Change Profile Photo"
                  >
                    <FaEdit className="w-4 h-4" />
                    <input 
                      id="profile-image-upload"
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </label>
                </div>
                 <span className="text-xs text-gray-500 mt-3 font-medium">Click icon to upload new photo</span>
              </div>

               <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Bio</label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-shadow bg-gray-50 focus:bg-white"
                  rows="4"
                  value={mentorForm.bio}
                  onChange={(e) => setMentorForm({ ...mentorForm, bio: e.target.value })}
                  placeholder="Tell students about yourself..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Expertise (Comma separated)</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-shadow bg-gray-50 focus:bg-white"
                  value={mentorForm.expertise}
                  onChange={(e) => setMentorForm({ ...mentorForm, expertise: e.target.value })}
                  placeholder="e.g. Web Development, AI, Career Advice"
                />
              </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Experience</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-shadow bg-gray-50 focus:bg-white"
                      value={mentorForm.experience}
                      onChange={(e) => setMentorForm({ ...mentorForm, experience: e.target.value })}
                      placeholder="e.g. 5 Years"
                    />
                  </div>
                   <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Mobile</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-shadow bg-gray-50 focus:bg-white"
                      value={mentorForm.mobile}
                      onChange={(e) => setMentorForm({ ...mentorForm, mobile: e.target.value })}
                    />
                  </div>
               </div>

               <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">LinkedIn URL</label>
                <div className="relative">
                   <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaLinkedin className="text-blue-600 text-lg" />
                   </div>
                   <input
                    type="url"
                    className="w-full pl-11 px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-shadow bg-gray-50 focus:bg-white"
                    value={mentorForm.linkedin}
                    onChange={(e) => setMentorForm({ ...mentorForm, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

               <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditMentorModalOpen(false)}
                  className="px-6 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-8 py-2.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-500/30 disabled:opacity-50 hover:translate-y-[-1px]"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;